import { EventEmitter, Injectable , NgZone} from '@angular/core';
import { AUTH_CONFIG } from './auth0-variables';
import { Router } from '@angular/router';
import 'rxjs/add/operator/filter';
import * as auth0 from 'auth0-js';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/mergeMap';
import {RedirectService} from '../../../utilities/services/redirect/redirect.service';
import {environment} from '../../../../../environments/environment';
import {Auth0DecodedHash, Auth0Error} from 'auth0-js';

@Injectable()
export class AuthService {
  refreshSubscription: any;

  auth0 = new auth0.WebAuth({
    clientID: AUTH_CONFIG.clientID,
    domain: AUTH_CONFIG.domain,
    responseType: 'token id_token',
    audience: AUTH_CONFIG.audience,
    redirectUri: AUTH_CONFIG.callbackURL,
    scope: 'openid email profile'
  });

  public onLogin = new EventEmitter();

  constructor(public router: Router, private redirectService: RedirectService, private ngZone: NgZone) {
    // allow trigger of refresh using the console window for debugging purposes.
    window['renewToken'] = () => this.renewToken();
    window['scheduleToken'] = (expiresAt?: number) => this.scheduleRenewal(expiresAt);
    window['scheduleTokenIn5Minutes'] = () =>  this.scheduleRenewal(Date.now() + (5.5 * 60 * 1000));
  }

  /**
   * Logs a user by redirecting them to the Auth0 hosted login page
   */
  public login(): void {
    this.auth0.authorize({});
  }

  /**
   * Parses the url hash sent back by Auth0
   */
  public handleAuthentication(): void {

    this.auth0.parseHash((err: Auth0Error, authResult: Auth0DecodedHash) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        localStorage.clear();
        // do not clear out the window.location.hash if we are navigating. When there is a guard that returns a delayed response
        // the router will not navigate to the given route.
        // the offending line has been removed. This comment is here to explain why and to prevent it from ever being added again.
        this.setSession(authResult);
        this.scheduleRenewal();
        this.onLogin.emit();
        this.router.navigate(['dashboard']);
      } else if (err) {
        this.logout();
        console.log(err);
        alert(`Error: ${err.error}. Check the console for further details.`);
      }
    });
  }

  /**
   * Logs a user out by removing the token related items
   * set in localStorage, and then navigating them to the
   * login page
   */
  public logout(): void {
    // Remove tokens and expiry time from localStorage
    localStorage.clear();
    const returnAddress = encodeURI(environment.appUrl + '/' + 'login');
    // redirect the user to Auth0 page in order to remove the SSO session
    // more info here: https://auth0.com/docs/logout
    this.redirectService.redirect(`https://lougeek.auth0.com/v2/logout?federated&returnTo=${returnAddress}`);
  }

  /**
   * Checks if a user is authenticated
   * @returns {boolean}
   */
  public isAuthenticated(): boolean {
    // Check whether the current time is past the
    // access token's expiry time
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return new Date().getTime() < expiresAt;
  }


  /**
   * Renews the token
   * @return A promise that resolves when completed
   */
  public renewToken() {
    return new Promise((resolve) => {
      this.auth0.renewAuth({
        redirectUri: environment.silentUrl,
        usePostMessage: true
      }, (err, result) => {
        if (err) {
          console.log(`Could not get a new token using silent authentication (${err.error}).`);
        } else {
          this.setSession(result);
          resolve();
        }
      });
    });

  }

  /**
   * Schedules the silent renewal of the token
   */
  public scheduleRenewal(expiresAt?: number) {
    // no need to schedule a renewal if the user is not authenticated
    if (!this.isAuthenticated()) return;
    // un-schedule the current renewal if it is scheduled.
    this.unscheduleRenewal();
    if (!expiresAt)
      expiresAt = JSON.parse(window.localStorage.getItem('expires_at'));

    const source = Observable.of(expiresAt).flatMap(
      expiresAt => {
        // schedule the renewal 5 minutes before it expires
        const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
        console.log(Math.floor((expiresAt - fiveMinutesFromNow) / 1000), 'seconds until renewal');
        // the smallest wait time is 2 seconds to allow for the auth iframe to load
        return Observable.timer(Math.max(2000 , expiresAt - fiveMinutesFromNow));
      });

    // run this observable outside of angular since it does not trigger any ui updates and it
    // breaks protractor
    this.ngZone.runOutsideAngular(() => {
      // Once the delay time from above is
      // reached, get a new JWT and schedule
      // additional refreshes
      this.refreshSubscription = source.subscribe(() => {
        console.log('calling renew Token');
        // renew the token and once it completes, schedule the new renewal
        this.renewToken().then(() => this.scheduleRenewal());
      });
    });

  }

  /**
   * Un-subscribes from the token renewal observable
   */
  public unscheduleRenewal() {
    if (!this.refreshSubscription) return;
    this.refreshSubscription.unsubscribe();
  }

  /**
   * Sets our session local storage
   * @param authResult
   */
  private setSession(authResult: Auth0DecodedHash): void {
    console.log(authResult);
    // Set the time that the access token will expire at
    const expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
    localStorage.setItem('access_token', authResult.accessToken);
    localStorage.setItem('id_token', authResult.idToken);
    localStorage.setItem('expires_at', expiresAt);
  }


}



// WEBPACK FOOTER //
// /var/www/dnata-cerf-front-end/releases/20200326161233/src/app/modules/user/services/auth/auth.service.ts